import { HttpHeaders } from "@angular/common/http";
import { environment } from "../../../../environments/environment";

/**
 * En-tête posé sur les requêtes du rafraîchissement AUTOMATIQUE du tableau de
 * bord. L'intercepteur de facturation de back-metier ne compte pas de user-jour
 * pour elles.
 *
 * Le défaut corrigé : un user-jour est compté à la PREMIÈRE requête
 * authentifiée du jour, et le tableau de bord re-télécharge ses widgets toutes
 * les 60 s tant que l'onglet est visible. Un écran de supervision laissé
 * ouvert facturait donc un user-jour par jour — week-ends compris — sans que
 * personne ne s'en serve.
 */
export const BACKGROUND_REFRESH_HEADER = "X-Background-Refresh";

/**
 * Durée maximale de la fenêtre. Le tick ne fait qu'incrémenter un signal : ce
 * sont les effects des widgets qui refont leurs GET, au cycle de détection
 * suivant (quelques millisecondes). 5 s couvrent largement la vague sans
 * déborder sur l'usage humain qui suit.
 */
const WINDOW_MS = 5_000;

/** Un geste de l'utilisateur referme la fenêtre : ses requêtes à lui comptent. */
const GESTURES = ["pointerdown", "keydown"] as const;

let openUntil = 0;

function close(): void {
  openUntil = 0;
  for (const g of GESTURES) {
    document.removeEventListener(g, close, true);
  }
}

/**
 * Portée « rafraîchissement automatique en arrière-plan ».
 *
 * Pourquoi une portée plutôt qu'un en-tête posé appel par appel : le tick
 * automatique déclenche une dizaine de chemins de chargement (le widget
 * générique, et sept widgets qui chargent eux-mêmes leurs données, dont trois
 * via l'API des non-conformités). Les marquer un par un, c'est un oubli au
 * prochain widget — et un seul GET non marqué suffit à compter la journée.
 *
 * Volontairement PAS ouverte au retour sur l'onglet (`visibilitychange`) :
 * revenir sur un onglet est un geste humain.
 */
export const BackgroundRefreshScope = {
  open(windowMs = WINDOW_MS): void {
    close();
    openUntil = Date.now() + windowMs;
    for (const g of GESTURES) {
      document.addEventListener(g, close, true);
    }
  },

  close,

  isOpen(): boolean {
    if (openUntil && Date.now() > openUntil) close();
    return openUntil > 0;
  },
};

/** La requête vise-t-elle back-metier (`environment.api()`) ? URL relative ou absolue. */
function targetsBackMetier(url: string): boolean {
  try {
    const origin = window.location.origin;
    const base = new URL(`${environment.api()}/`, origin).href;
    return new URL(url, origin).href.startsWith(base);
  } catch {
    return false;
  }
}

/**
 * Pose `X-Background-Refresh: 1` si la requête part vers back-metier pendant
 * une vague de rafraîchissement automatique (appelée par
 * `BackgroundRefreshInterceptor`). Les autres API ne sont pas touchées : elles
 * ne facturent rien à l'usage, et un en-tête inconnu y déclencherait un pré-vol
 * CORS pour rien. Rend `headers` inchangé (même instance) hors portée.
 */
export function withBackgroundRefreshHeader(
  url: string,
  headers: HttpHeaders,
): HttpHeaders {
  if (!BackgroundRefreshScope.isOpen() || !targetsBackMetier(url)) {
    return headers;
  }
  return headers.set(BACKGROUND_REFRESH_HEADER, "1");
}
